import { FC, useEffect, useMemo, useState } from 'react';

import { Transaction } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Expand } from '@ravago/shared/radiance/components/expand/components/Expand/Expand';
import { Pagination } from '@ravago/shared/radiance/components/pagination/components/Pagination/Pagination';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useTranslations } from '@ravago/shared/translations/client';

import { OpenInvoiceFilters } from '../../../models/filters.model';
import { InvoiceFilterUtils } from '../../../utils/invoice-filter.utils';

import { InvoicePaymentSummary } from './invoice-payment-summary';
import { InvoiceSummary } from './invoice-summary';
import { OpenInvoiceTableDesktop } from './table/desktop/open-invoice-table-desktop';
import { OpenInvoiceTableMobile } from './table/mobile/open-invoice-table-mobile';

type Props = {
   currency: string;
   openInvoices: Transaction[];
   openInvoiceFilters: OpenInvoiceFilters;
   selectedInvoiceIds: string[];
   isExpanded: boolean;
   canPayOnline: boolean;
   canDeductCreditNotes: boolean;
   onExpandToggle: () => void;
   onSelectedInvoiceIdsChange: (invoiceIds: string[]) => void;
   onPay: (type: 'selected' | 'open' | 'overdue') => void;
};

const INVOICES_PER_PAGE = 10;

export const InvoiceCollapsible: FC<Props> = ({
   currency,
   canPayOnline,
   canDeductCreditNotes,
   isExpanded,
   openInvoices,
   openInvoiceFilters,
   selectedInvoiceIds,
   onExpandToggle,
   onSelectedInvoiceIdsChange,
   onPay
}) => {
   const [currentPage, setCurrentPage] = useState(1);
   const translate = useTranslations();
   const { desktop } = useBreakpoint('desktop');

   const areOpenInvoicesFiltered =
      !!openInvoiceFilters.eInvoiceId ||
      !!openInvoiceFilters.dueDate ||
      openInvoiceFilters.overdue ||
      openInvoiceFilters.documentTypes.some((type) => type.checked);

   const filteredInvoices = useMemo(
      () => InvoiceFilterUtils.filterOpenInvoiceTransactions(openInvoices, openInvoiceFilters),
      [openInvoices, openInvoiceFilters]
   );

   const totalPages = useMemo(() => Math.ceil(filteredInvoices.length / INVOICES_PER_PAGE), [filteredInvoices]);
   const pagedInvoices = useMemo(
      () => filteredInvoices.slice((currentPage - 1) * INVOICES_PER_PAGE, currentPage * INVOICES_PER_PAGE),
      [currentPage, filteredInvoices]
   );

   const handleExpandToggle = () => {
      onExpandToggle();
   };

   const handleInvoiceSelect = (invoiceId: string) => {
      const foundInvoice = filteredInvoices.find((invoice) => invoice.id === invoiceId);
      if (!foundInvoice) return;
      onSelectedInvoiceIdsChange([...selectedInvoiceIds, invoiceId]);
   };

   const handleInvoiceDeselect = (invoiceId: string) => {
      onSelectedInvoiceIdsChange(selectedInvoiceIds.filter((i) => i !== invoiceId));
   };

   const handleSelectAll = (invoiceIds: string[]) => {
      const newInvoiceIds = invoiceIds.filter((invoiceId) => !selectedInvoiceIds.includes(invoiceId));
      onSelectedInvoiceIdsChange([...selectedInvoiceIds, ...newInvoiceIds]);
   };

   const handleDeselectAll = (invoiceIds: string[]) => {
      onSelectedInvoiceIdsChange(selectedInvoiceIds.filter((invoiceId) => !invoiceIds.includes(invoiceId)));
   };

   const handlePayClicked = (type: 'selected' | 'open' | 'overdue') => {
      onPay(type);
   };

   useEffect(() => {
      setCurrentPage(1);
   }, [filteredInvoices]);

   return (
      <Paper shadow>
         <Expand
            expanded={isExpanded}
            header={
               <Box direction={desktop ? 'row' : 'column-reverse'} justify="between" items="stretch" margin={{ top: 'md', bottom: 'md' }}>
                  {canPayOnline ? (
                     <InvoicePaymentSummary
                        currency={currency}
                        openInvoices={openInvoices}
                        filteredOpenInvoices={filteredInvoices}
                        selectedInvoiceIds={selectedInvoiceIds}
                        canDeductCreditNotes={canDeductCreditNotes}
                        onPayClick={handlePayClicked}
                     />
                  ) : (
                     <InvoiceSummary
                        currency={currency}
                        openInvoices={openInvoices}
                        filteredOpenInvoices={filteredInvoices}
                        areOpenInvoicesFiltered={areOpenInvoicesFiltered}
                     />
                  )}
                  <Box justify={desktop ? 'start' : 'end'}>
                     <IconButton ariaLabel="expand" icon={isExpanded ? 'chevron-up' : 'chevron-down'} onClick={handleExpandToggle} />
                  </Box>
               </Box>
            }
         >
            {filteredInvoices.length ? (
               <Box direction="column" margin={{ top: 'sm' }}>
                  <BreakpointSwitch includeViews={['desktop']}>
                     <OpenInvoiceTableDesktop
                        currency={currency}
                        invoices={pagedInvoices}
                        canPayOnline={canPayOnline}
                        selectedInvoiceIds={selectedInvoiceIds}
                        onInvoiceSelect={handleInvoiceSelect}
                        onInvoiceDeselect={handleInvoiceDeselect}
                        onSelectAll={handleSelectAll}
                        onDeselectAll={handleDeselectAll}
                     />
                  </BreakpointSwitch>
                  <BreakpointSwitch excludeViews={['desktop']}>
                     <OpenInvoiceTableMobile
                        currency={currency}
                        invoices={pagedInvoices}
                        canPayOnline={canPayOnline}
                        selectedInvoiceIds={selectedInvoiceIds}
                        onInvoiceSelect={handleInvoiceSelect}
                        onInvoiceDeselect={handleInvoiceDeselect}
                     />
                  </BreakpointSwitch>
                  <Box justify="end" margin={{ top: 'sm' }}>
                     <Pagination current={currentPage} onChange={setCurrentPage} total={totalPages} truncate />
                  </Box>
               </Box>
            ) : (
               <Box direction="column" gap="md" margin={{ top: 'sm' }}>
                  <Divider />
                  <Typography>{translate('invoices.empty-state.no-documents-found')}</Typography>
               </Box>
            )}
         </Expand>
      </Paper>
   );
};
